import * as XLSX from "xlsx";
import { DateTime } from "luxon";

// ST1-2060 — formats de nombre Excel par type de colonne. Les valeurs restent
// des nombres (ou de vraies dates) : c'est le format qui porte l'affichage
// (séparateurs, décimales, %), Excel le localise lui-même (virgule décimale en
// français). Auparavant les montants partaient en chaînes « 393,17 », donc en
// cellules texte inexploitables dans un calcul.
const NUMBER_FORMATS = {
  currency: "#,##0.00",
  percentage: "0.00%",
  distance: "#,##0",
  duration: "0",
  number: "#,##0.00",
};

const DATE_TYPES = ["date", "date_debut", "date_fin"];
const DATE_FORMAT = "dd/mm/yyyy";
const DATE_DISPLAY_LENGTH = DATE_FORMAT.length;

/**
 * Service pour l'export Excel des données de flotte
 */
const excelExportService = {
  /**
   * Exporte les données de flotte en fichier Excel
   * @param {Array} data - Données à exporter
   * @param {Array} visibleHeaders - Headers visibles (configuration des colonnes)
   * @param {String} filename - Nom du fichier (optionnel)
   */
  exportToExcel: (data, visibleHeaders, filename = "export-flotte") => {
    try {
      const workbook = XLSX.utils.book_new();
      const worksheet = excelExportService.buildWorksheet(data, visibleHeaders);

      // Ajouter la feuille au workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Flotte");

      // Générer le nom du fichier avec timestamp
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const fullFilename = `${filename}_${timestamp}.xlsx`;

      // Télécharger le fichier
      XLSX.writeFile(workbook, fullFilename);

      return { success: true, filename: fullFilename };
    } catch (error) {
      console.error("Erreur lors de l'export Excel:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Construit la feuille Excel : une colonne par header (dans l'ordre reçu),
   * cellules typées (nombre, date, texte) et formats de nombre posés par
   * colonne. Séparé de l'écriture du fichier pour être testable.
   * @param {Array} data - Données brutes
   * @param {Array} visibleHeaders - Headers visibles
   * @returns {Object} Worksheet SheetJS
   */
  buildWorksheet: (data, visibleHeaders) => {
    const excelData = excelExportService.prepareDataForExcel(
      data,
      visibleHeaders,
    );

    // cellDates : les objets Date deviennent de vraies dates Excel (type "d").
    const worksheet = XLSX.utils.json_to_sheet(excelData, {
      header: visibleHeaders.map((header) => header.title),
      cellDates: true,
    });

    excelExportService.applyColumnFormats(worksheet, visibleHeaders);

    worksheet["!cols"] = excelExportService.calculateColumnWidths(
      visibleHeaders,
      excelData,
    );

    return worksheet;
  },

  /**
   * Pose le format d'affichage (nombre, pourcentage, date) sur chaque cellule
   * de données des colonnes concernées — l'en-tête (ligne 1) est ignoré.
   * @param {Object} worksheet - Worksheet SheetJS
   * @param {Array} visibleHeaders - Headers visibles (même ordre que les colonnes)
   */
  applyColumnFormats: (worksheet, visibleHeaders) => {
    if (!worksheet["!ref"]) return;
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    visibleHeaders.forEach((header, c) => {
      const format = excelExportService.numberFormatFor(header.type);
      if (!format) return;

      for (let r = 1; r <= range.e.r; r++) {
        const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
        if (cell && (cell.t === "n" || cell.t === "d")) {
          cell.z = format;
        }
      }
    });
  },

  /**
   * Format de nombre Excel d'un type de colonne, ou null s'il n'en a pas (texte).
   * @param {String} type - Type de colonne (columnsConfig)
   * @returns {String|null}
   */
  numberFormatFor: (type) => {
    if (DATE_TYPES.includes(type)) return DATE_FORMAT;
    return NUMBER_FORMATS[type] || null;
  },

  /**
   * Prépare les données pour l'export Excel en formatant les valeurs
   * @param {Array} data - Données brutes
   * @param {Array} visibleHeaders - Headers visibles
   * @returns {Array} Données formatées pour Excel
   */
  prepareDataForExcel: (data, visibleHeaders) => {
    return data.map((item) => {
      const row = {};

      visibleHeaders.forEach((header) => {
        // Utiliser le path pour accéder aux données imbriquées
        const value = excelExportService.getValueFromPath(
          item,
          header.path || header.field,
        );

        // Formater la valeur selon le type de colonne
        row[header.title] = excelExportService.formatValueForExcel(
          value,
          header,
        );
      });

      return row;
    });
  },

  /**
   * Récupère une valeur depuis un chemin d'accès dot-notation
   * @param {Object} obj - Objet source
   * @param {String} path - Chemin d'accès (ex: "contrat.client.nom")
   * @returns {*} Valeur trouvée
   */
  getValueFromPath: (obj, path) => {
    if (!obj || !path) return null;

    return path.split(".").reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null;
    }, obj);
  },

  /**
   * Formate une valeur selon son type pour l'export Excel.
   * Renvoie null pour une cellule vide (jamais une chaîne vide, qui ferait
   * une cellule texte), un nombre pour les colonnes numériques, un objet Date
   * pour les colonnes de dates, une chaîne pour le reste.
   * @param {*} value - Valeur à formater
   * @param {Object} header - Configuration de la colonne
   * @returns {*} Valeur formatée
   */
  formatValueForExcel: (value, header) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (DATE_TYPES.includes(header.type)) {
      return excelExportService.parseDate(value);
    }

    if (NUMBER_FORMATS[header.type]) {
      return excelExportService.parseNumber(value);
    }

    switch (header.type) {
      case "boolean":
        return value ? "Oui" : "Non";

      case "badge":
      case "brand":
      case "status":
      case "select":
      case "text":
      case "condensed":
      default:
        return String(value);
    }
  },

  /**
   * Nombre à partir d'une valeur numérique ou d'une chaîne (point ou virgule
   * décimale). Une chaîne illisible est conservée telle quelle (texte) plutôt
   * que perdue.
   * @param {*} value
   * @returns {number|string}
   */
  parseNumber: (value) => {
    if (typeof value === "number") return value;
    const parsed = Number(String(value).trim().replace(/\s/g, "").replace(",", "."));
    return Number.isNaN(parsed) ? String(value) : parsed;
  },

  /**
   * Date locale (minuit) à partir d'une date ISO ou « jj-mm-aaaa » (date de
   * commande) ; null si illisible. Le jour est lu dans le fuseau de la valeur
   * (setZone) puis reconstruit en local : Excel affiche le même jour que l'API,
   * quel que soit le fuseau du poste.
   * @param {*} value
   * @returns {Date|null}
   */
  parseDate: (value) => {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    const raw = String(value).trim();
    const candidates = [
      DateTime.fromISO(raw, { setZone: true }),
      DateTime.fromFormat(raw, "dd-MM-yyyy"),
      DateTime.fromFormat(raw, "dd/MM/yyyy"),
    ];
    const dt = candidates.find((d) => d.isValid);
    return dt ? new Date(dt.year, dt.month - 1, dt.day) : null;
  },

  /**
   * Calcule la largeur optimale des colonnes pour Excel
   * @param {Array} visibleHeaders - Headers visibles
   * @param {Array} data - Données
   * @returns {Array} Configuration des largeurs de colonnes
   */
  calculateColumnWidths: (visibleHeaders, data) => {
    return visibleHeaders.map((header) => {
      // Largeur basée sur le titre de la colonne
      let maxWidth = header.title.length;

      // Vérifier quelques échantillons de données pour estimer la largeur
      const sampleSize = Math.min(10, data.length);
      for (let i = 0; i < sampleSize; i++) {
        const cellValue = data[i][header.title];
        const length =
          cellValue instanceof Date
            ? DATE_DISPLAY_LENGTH
            : String(cellValue ?? "").length;
        maxWidth = Math.max(maxWidth, length);
      }

      // Limiter la largeur entre 10 et 50 caractères
      const width = Math.min(Math.max(maxWidth + 2, 10), 50);

      return { width };
    });
  },
};

export default excelExportService;
